<template>
    <section
        class="slice slice-trends-quadrant"
        :class="{'slice-trends-quadrant--grey': backgroundColor === 'grey'}"
        data-testid="slice-trends-quadrant"
    >
        <div class="slice-content">
            <TitleBrand :field="slice.primary.title" />
            <h3 v-if="slice.primary.subtitle">
                {{ slice.primary.subtitle }}
            </h3>
            <div class="slice-trends-quadrant__image-wrapper">
                <Transition name="fade" mode="out-in">
                    <div :key="imageIndex" class="slice-trends-quadrant__images">
                        <MaltImg
                            :src="currentTopImage?.url ?? ''"
                            :alt="currentTopImage?.alt ?? ''"
                            height="250"
                            class="slice-trends-quadrant__image"
                            :data-testid="`slice-trends-quadrant-image-top-${imageIndex}`"
                        />
                        <MaltImg
                            :src="currentBottomImage?.url ?? ''"
                            :alt="currentBottomImage?.alt ?? ''"
                            height="250"
                            class="slice-trends-quadrant__image"
                            :data-testid="`slice-trends-quadrant-image-bottom-${imageIndex}`"
                        />
                    </div>
                </Transition>
            </div>
            <div v-if="slice.primary.filteredQuadrants.length" class="slice-trends-quadrant__pills-wrapper">
                <button
                    type="button"
                    :class="[
                        'slice-trends-quadrant__pill',
                        'slice-trends-quadrant__pill-all',
                        {'slice-trends-quadrant__pill-all--active': selectedIndex === null},
                    ]"
                    data-testid="slice-trends-quadrant-pill-all"
                    @click="selectedIndex = null"
                >
                    {{ $t('general.all') }}
                </button>
                <button
                    v-for="(quadrant, index) in slice.primary.filteredQuadrants"
                    :key="index"
                    type="button"
                    :class="[
                        'slice-trends-quadrant__pill',
                        `slice-trends-quadrant__pill-${index}`,
                        {[`slice-trends-quadrant__pill-${index}--active`]: selectedIndex === index},
                    ]"
                    :data-testid="`slice-trends-quadrant-pill-${index}`"
                    @click="selectedIndex = index"
                >
                    {{ quadrant.pill }}
                </button>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import type {Content, ImageField} from '@prismicio/client';
    import {getSliceComponentProps} from '@prismicio/vue';

    import {MaltImg} from '#components';
    import TitleBrand from '~/components/TitleBrand.vue';

    const props = defineProps(getSliceComponentProps<Content.TrendsQuadrantSlice>(['slice', 'index', 'slices', 'context']));

    const topImages = computed<ImageField[]>(() => [
        props.slice.primary.mobileTopDefaultImage,
        ...props.slice.primary.filteredQuadrants.map((img) => img.mobileTopImage),
    ]);
    const bottomImages = computed<ImageField[]>(() => [
        props.slice.primary.mobileBottomDefaultImage,
        ...props.slice.primary.filteredQuadrants.map((img) => img.mobileBottomImage),
    ]);

    const selectedIndex = ref<number | null>(null);

    const imageIndex = computed(() => (selectedIndex.value === null ? 0 : selectedIndex.value + 1));
    const currentTopImage = computed(() => topImages.value[imageIndex.value]);
    const currentBottomImage = computed(() => bottomImages.value[imageIndex.value]);
    const backgroundColor = computed(() => props.slice.primary.backgroundColor);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-trends-quadrant {
        &--grey {
            background-color: var(--joy-color-neutral-20);
        }

        h3 {
            font-size: var(--joy-font-size-primary-800);
            margin-bottom: var(--joy-core-spacing-6);
        }

        &__image-wrapper {
            display: flex;
            justify-content: center;
            align-items: center;
            background-color: var(--joy-color-background-default);
            border-radius: var(--joy-core-radius-4);
            width: 100%;
            box-shadow: var(--joy-core-elevation-0);
            border: 2px solid var(--joy-color-neutral-20);
            height: auto;

            @include mq.screen_sm(min) {
                min-height: 400px;
            }
        }

        &__images {
            display: flex;
            flex-direction: column;
            justify-content: center;
            padding: var(--joy-core-spacing-7);
            height: 100%;

            @include mq.screen_sm(min) {
                flex-direction: row;
            }
        }

        &__image {
            min-height: 250px;
            display: block;
            object-fit: contain;
            flex-shrink: 1;
            width: 100%;
            min-width: 0;
            max-height: 100%;

            @include mq.screen_sm(min) {
                height: 400px;
            }
        }
        &__pills-wrapper {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            width: 100%;
            gap: var(--joy-core-spacing-5);
            margin-top: var(--joy-core-spacing-6);

            @include mq.screen_sm(min) {
                gap: var(--joy-core-spacing-8);
            }
        }

        &__pill {
            position: relative;
            user-select: none;
            color: var(--joy-color-neutral-60);
            font-size: var(--joy-font-size-primary-400);
            font-weight: var(--joy-font-weight-bold);
            padding: var(--joy-core-spacing-2) var(--joy-core-spacing-4);
            border-radius: var(--joy-core-spacing-6);
            cursor: pointer;
            transition: background-color var(--joy-transition-duration-default) ease;

            &::before {
                content: '';
                position: absolute;
                inset: 0;
                border-radius: inherit;
                border: 2px solid transparent;
                pointer-events: none;
                transition: border-color var(--joy-transition-duration-default) ease;
            }

            &-all {
                background-color: var(--joy-color-neutral-30);

                &--active::before {
                    border-color: var(--joy-color-neutral-40);
                }

                &:not(&--active):hover {
                    background-color: var(--joy-color-neutral-40);
                }
            }

            &-0 {
                background-color: var(--joy-color-brand-primary-30);

                &--active::before {
                    border-color: var(--joy-color-brand-primary-10);
                }

                &:not(&--active):hover {
                    background-color: var(--joy-color-brand-primary-70);
                }
            }

            &-1 {
                background-color: var(--joy-color-tertiary-50);

                &--active::before {
                    border-color: var(--joy-color-tertiary-30);
                }
                &:not(&--active):hover {
                    background-color: var(--joy-color-tertiary-70);
                }
            }

            &-2 {
                background-color: var(--joy-color-primary-50);

                &--active::before {
                    border-color: var(--joy-color-primary-30);
                }
                &:not(&--active):hover {
                    background-color: var(--joy-color-primary-70);
                }
            }
        }
    }

    .fade-enter-active,
    .fade-leave-active {
        transition: opacity var(--joy-transition-duration-default) var(--joy-transition-timing-function);
    }

    .fade-enter-from,
    .fade-leave-to {
        opacity: 0;
    }

    .fade-enter-to,
    .fade-leave-from {
        opacity: 1;
    }
</style>
